import React, {
  useMemo,
  useContext,
  useEffect,
  useState,
  useRef,
} from 'react';
import useSWR from 'swr';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useHistory } from 'react-router';
import { UserService, IUserDataResponse } from '../services/user/user.service';
import { RootState } from '../../store/create-store';
import { AuthService, IUserAuthData } from '../services/auth/auth.service';
import { setAuth, setUserData, clearLocalAuth } from '../../store/common/common.reducer';
import Loader from '../components/loader/loader.component';
import useAlert from '../hooks/useAlert';
import { AlertVariant } from '../providers/alert.provider';
import { isDevAuthBypass } from '../dev/dev-auth-bypass';
import {
  createMockMemberUser,
  MOCK_AUTH_TOKEN,
} from '../dev/mock-member-user';
import {
  requiresOnboarding,
  resolvePostAuthDestination,
} from '../routing/onboarding-routing';
import {
  isPublicVisitorPath,
  shouldRedirectToOnboarding,
} from '../../app/premium-profile/profile-path';
import { getKeycloakRedirectUri } from '../../environments';
import {
  clearRenewalRedirectUri,
  learnClockSkew,
  readPostAuthReturn,
  readRenewalRedirectUri,
  readTopLevelRenewal,
  takePostAuthReturn,
} from '../lib/session-renewal';
import { reservedLoginMessage } from './login-failure';
/** Keycloak auth codes are single-use — never exchange the same code twice. */
const exchangedAuthCodes = new Set<string>();

/** Skip sync only while a Keycloak code is still being exchanged. */
export function shouldDeferLoggedInUserFetch({
  authCode,
  exchangeResult,
  exchangeError,
}: {
  authCode?: string | null;
  exchangeResult?: unknown;
  exchangeError?: unknown;
}): boolean {
  return Boolean(authCode) && !exchangeResult && !exchangeError;
}

/** The redirect_uri to redeem a Keycloak code with: the one its own trip was sent with. */
export function exchangeRedirectUri(returnedState?: string | null): string {
  return (
    readRenewalRedirectUri(returnedState) ||
    localStorage.getItem('redirect_uri') ||
    getKeycloakRedirectUri()
  );
}

interface ReturnTarget {
  pathname: string;
  search: string;
  hash: string;
}

function returnTargetOf(path: string): ReturnTarget | null {
  if (!path.startsWith('/') || path.startsWith('//')) return null;
  try {
    const url = new URL(path, window.location.origin);
    if (url.origin !== window.location.origin) return null;
    if (url.pathname === '/redirect') return null;
    return { pathname: url.pathname, search: url.search, hash: url.hash };
  } catch {
    return null;
  }
}

export interface UserDataContextInterface extends Partial<IUserDataResponse> {
  auth: any | null;
  isLoggedIn: boolean;
  refreshUserData: () => Promise<IUserDataResponse | null | undefined>;
}

export const UserDataContext = React.createContext<UserDataContextInterface>({
  user: undefined,
  auth: null,
  isLoggedIn: false,
  diffDays: 0,
  refreshUserData: async () => undefined,
});

console.log(UserDataContext);

const UserDataProvider = ({ children }: any) => {
  const commonState = useSelector((state: RootState) => state.common);
  const { auth, user } = commonState;

  const userService = new UserService();

  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();
  const { showAlert } = useAlert();

  const { search, hash = '' } = location;
  const hashParams = new URLSearchParams(hash.replace('#', '?'));
  const params = useMemo(() => new URLSearchParams(search), []);

  const token = useMemo(() => params.get('token'), []);
  const code = hashParams.get('code');
  const returnedState = hashParams.get('state');
  const loginInFlightRef = useRef<string | null>(null);
  const renewalRef = useRef(readTopLevelRenewal(returnedState));
  const renewalReturnRef = useRef(
    renewalRef.current ? returnTargetOf(readPostAuthReturn()) : null,
  );

  const finishRenewal = (restoreAppRedirect: boolean) => {
    const renewal = renewalRef.current;
    if (!renewal) return;
    renewalRef.current = null;
    clearRenewalRedirectUri();
    takePostAuthReturn();
    if (restoreAppRedirect && renewal.appRedirectUri) {
      localStorage.setItem('redirect_uri', renewal.appRedirectUri);
    }
  };

  const onboardingTarget = (): ReturnTarget => {
    const back = renewalRef.current ? renewalReturnRef.current : null;
    finishRenewal(true);
    return back?.pathname.startsWith('/onboarding')
      ? { ...back, pathname: '/onboarding' }
      : { pathname: '/onboarding', search, hash: '' };
  };

  const redirectUri = useMemo(
    () => localStorage.getItem('redirect_uri') || params.get('redirect_uri'),
    [],
  );

  const stripAuthCodeFromUrl = () => {
    if (!location.hash || !hashParams.get('code')) return;
    history.replace({
      ...location,
      hash: '',
    });
  };

  const recoverFromStaleAuthCallback = () => {
    dispatch(clearLocalAuth());
    showAlert(
      'Sign-in could not finish. Ensure the local API is running on port 3000, then try Login again.',
      AlertVariant.ERROR,
    );
    history.replace('/');
  };

  useEffect(() => {
    if (token) {
      history.replace({
        ...location,
        hash: '',
        pathname: location.pathname === '/' ? '/u' : location.pathname,
      });
    }
  }, [token]);

  useEffect(() => {
    if (isDevAuthBypass && !auth) {
      dispatch(setAuth({ token: MOCK_AUTH_TOKEN } as IUserAuthData));
      dispatch(setUserData(createMockMemberUser()));
    }
  }, [auth, dispatch]);

  const {
    data: authData,
    error: authError,
  } = useSWR(
    {
      key: 'performLogin',
      code,
    },
    filters => {
      const nextCode = filters.code;
      if (!nextCode) return null;

      // Skip re-exchange (Strict Mode remount / stale hash after success).
      if (
        exchangedAuthCodes.has(nextCode) ||
        loginInFlightRef.current === nextCode
      ) {
        return null;
      }

      loginInFlightRef.current = nextCode;
      const exchangeUri = exchangeRedirectUri(returnedState);
      return new AuthService()
        .login(
          nextCode,
          exchangeUri,
          commonState?.redirectURI?.refCode || '',
        )
        .then(result => {
          exchangedAuthCodes.add(nextCode);
          learnClockSkew(result?.token);
          return result;
        })
        .finally(() => {
          if (loginInFlightRef.current === nextCode) {
            loginInFlightRef.current = null;
          }
        });
    },
    {
      revalidateOnFocus: false,
      shouldRetryOnError: false,
    },
  );

  useEffect(() => {
    if (authData || (typeof token === 'string' && token)) {
      dispatch(setAuth(authData || ({ token } as IUserAuthData)));
    }
  }, [authData, token]);

  const deferAuthFetch = shouldDeferLoggedInUserFetch({
    authCode: code,
    exchangeResult: authData,
    exchangeError: authError,
  });

  const {
    data: userData,
    error: userError,
    isValidating,
    mutate,
  } = useSWR(
    { key: 'getLoggedInUser', auth, deferAuthFetch },
    filters => {
      if (filters.deferAuthFetch) {
        return null;
      }
      return filters.auth && !isDevAuthBypass
        ? userService.getUserData(false)
        : null;
    },
    {
      revalidateOnFocus: false,
      shouldRetryOnError: false,
    },
  );

  const clearAuthHash = () => {
    if (!location.hash) return;
    history.replace({
      ...location,
      hash: '',
    });
  };

  useEffect(() => {
    if (userData) {
      dispatch(setUserData(userData.user));
      if (shouldRedirectToOnboarding(requiresOnboarding(userData.user), location.pathname)) {
        // Already in the flow — do not wipe #step=... (e.g. after refreshUserData
        // from profile-picture upload). Clearing hash resets to referring-channel
        // and referred users auto-skip to gift-card-reward.
        history.replace(onboardingTarget());
      }
    }
  }, [userData, history, location.pathname, search]);

  const [isLoggedIn, setIsLoggedIn] = useState(false);

  useEffect(() => {
    if (isDevAuthBypass && user) {
      setIsLoggedIn(true);
      return;
    }
    if (user && userData && user._id === userData.user._id) {
      setIsLoggedIn(true);
    }
  }, [user, userData]);

  useEffect(() => {
    if (!isLoggedIn || !user || !userData) return;
    if (location.pathname.startsWith('/onboarding')) return;

    const onAuthCallback =
      Boolean(code) || location.pathname === '/redirect';
    if (!onAuthCallback) return;

    // Keycloak returns to `/redirect` with `#code=...`.
    // Incomplete members go to onboarding; completed members go to dashboard.
    const destination = resolvePostAuthDestination({
      user,
      redirectUri,
      origin: window.location.origin,
    });

    if (destination.kind === 'onboarding') {
      history.replace(onboardingTarget());
      return;
    }

    const back = renewalRef.current ? renewalReturnRef.current : null;
    finishRenewal(true);
    if (back) {
      history.replace(back);
      return;
    }

    if (destination.kind === 'external') {
      localStorage.removeItem('redirect_uri');
      window.location.href = destination.href;
      return;
    }

    localStorage.removeItem('redirect_uri');
    const returnPath = takePostAuthReturn();
    if (returnPath.startsWith('/u')) {
      const returnUrl = new URL(returnPath, window.location.origin);
      history.replace({
        pathname: returnUrl.pathname,
        search: returnUrl.search,
        hash: '',
      });
      return;
    }
    history.replace({ pathname: destination.pathname, search: '', hash: '' });
  }, [isLoggedIn, user, userData, code, location.pathname]);

  useEffect(() => {
    const reservedName = reservedLoginMessage(authError);
    if (authError?.message === 'Unknown signUpSource' || reservedName) {
      if (code) exchangedAuthCodes.add(code);
      stripAuthCodeFromUrl();
      showAlert(reservedName || 'Unknown signUpSource', AlertVariant.ERROR);
      history.replace('/');
      return;
    }

    if (authError) {
      const onAuthCallback = location.pathname === '/redirect';
      if (code) exchangedAuthCodes.add(code);
      stripAuthCodeFromUrl();
      console.error(authError);
      // Stale persisted token is not valid when the Keycloak exchange failed.
      if (onAuthCallback && !authData?.token) {
        recoverFromStaleAuthCallback();
        return;
      }
      const alreadyAuthed = Boolean(auth?.token || authData?.token);
      const onOnboarding = location.pathname.startsWith('/onboarding');
      // Do not bounce a successful signup off onboarding when a duplicate
      // code exchange (Strict Mode / remount) fails with 401.
      if (alreadyAuthed || onOnboarding || isPublicVisitorPath(location.pathname)) {
        return;
      }
      showAlert('Something went wrong', AlertVariant.ERROR);
      history.replace('/');
    }

    if (userError) {
      const alreadyAuthed = Boolean(auth?.token || authData?.token);
      const onOnboarding = location.pathname.startsWith('/onboarding');
      const onAuthCallback = location.pathname === '/redirect';
      console.error(userError);
      if (onAuthCallback && !userData) {
        recoverFromStaleAuthCallback();
        return;
      }
      if (alreadyAuthed || onOnboarding || isPublicVisitorPath(location.pathname)) {
        return;
      }
      history.replace('/');
      showAlert('Something went wrong', AlertVariant.ERROR);
    }
  }, [authError, userError]);

  const contextValue: UserDataContextInterface = useMemo(() => {
    return {
      ...userData,
      user: userData?.user ?? user ?? undefined,
      auth,
      isLoggedIn,
      refreshUserData: mutate,
    };
  }, [userData, user, auth, authData, mutate, isLoggedIn]);

  const blockOnUserFetch = Boolean(
    auth && isValidating && !user && !isDevAuthBypass,
  );
  const blockOnAuth = Boolean(code && !auth && !authError && !isDevAuthBypass);

  return (
    <UserDataContext.Provider value={contextValue}>
      {blockOnUserFetch || blockOnAuth ? (
        <Loader showLoader />
      ) : (
        children
      )}
    </UserDataContext.Provider>
  );
};

function useUserData() {
  const userData = useContext(UserDataContext);
  return userData;
}

export { useUserData };

export default UserDataProvider;
